package fr.frux.feature.site.detail

import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.allowsContribution
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.SiteRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SiteHarvestsUi(
    val upcoming: List<Harvest> = emptyList(),
    val isLoading: Boolean = false,
)

/** Suite donnée à « Lancer une récolte » : le formulaire, la connexion ou rien (lieu privé d'autrui). */
enum class StartOutcome { Open, LoginRequired, Locked }

/**
 * Récoltes à venir d'un lieu et droit de lancer la sienne. Sorti du ViewModel de la fiche pour
 * tenir son plafond de fonctions (detekt), sur le patron que suivront les autres gestes du lot 3.
 */
class SiteHarvestsController(
    private val harvestRepository: HarvestRepository,
    private val siteRepository: SiteRepository,
    private val scope: CoroutineScope,
    private val isSignedIn: () -> Boolean,
    // Rien à rafraîchir tant que la fiche n'a pas son premier chargement : le ViewModel s'en charge.
    private val hasSite: () -> Boolean,
    // Le lieu rafraîchi retourne à la fiche, qui recharge aussi les récoltes.
    private val onSiteRefreshed: (Site) -> Unit,
) {
    private val _state = MutableStateFlow(SiteHarvestsUi())
    val state: StateFlow<SiteHarvestsUi> = _state.asStateFlow()

    private var loadJob: Job? = null
    private var refreshJob: Job? = null

    /** Ne garde que les récoltes qui accueillent encore du monde, la plus proche d'abord. */
    fun load(siteId: Long) {
        loadJob?.cancel()
        _state.update { it.copy(isLoading = true) }
        loadJob =
            scope.launch {
                harvestRepository
                    .getHarvestsForSite(siteId)
                    .onSuccess { harvests ->
                        val upcoming = harvests.filter { it.status.acceptsParticipants && !it.expired }
                        _state.value = SiteHarvestsUi(upcoming.sortedBy { it.scheduledDate })
                    }.onFailure { error ->
                        // La fiche reste lisible sans ses récoltes : on garde ce qui était affiché.
                        Logger.i(TAG, "Récoltes du lieu $siteId indisponibles : ${error.message}")
                        _state.update { it.copy(isLoading = false) }
                    }
            }
    }

    /**
     * Au retour sur la fiche : une récolte créée entre-temps passe par `HarvestRepository`, qui n'annonce
     * rien sur `SiteRepository.changes`. Le lieu est relu pour réévaluer le verrou, puis la liste.
     */
    fun refresh(siteId: Long) {
        if (!hasSite() || refreshJob?.isActive == true) return
        refreshJob =
            scope.launch {
                siteRepository
                    .refreshSite(siteId)
                    .onSuccess(onSiteRefreshed)
                    .onFailure {
                        Logger.i(TAG, "Lieu $siteId non rafraîchi au retour : ${it.message}")
                        load(siteId)
                    }
            }
    }

    fun requestStart(site: Site): StartOutcome =
        when {
            !isSignedIn() -> StartOutcome.LoginRequired
            site.allowsContribution() -> StartOutcome.Open
            else -> StartOutcome.Locked
        }

    private companion object {
        const val TAG = "SiteHarvestsController"
    }
}
